import { useEffect, useRef, useState } from 'react';
import { getInstrumentIdForce, InstrumentStore, ListedInstrument } from '../../types';
import { getSubscribeOptions } from './eStreamUtils';
import { InstrumentSubscriptionProps } from './useInstrumentStoreItem';
import { SubscribeToInstrumentChangesFn } from './useSubscribeToInstrumentChanges';

/**
 * Manages eStream subscription and atom tracking for an instrument
 * without reading the atom value (no re-renders from atom changes).
 *
 * Returns the current instrument atom so callers can read it independently.
 */
export const useInstrumentSubscription = (
  instrument: ListedInstrument | null,
  instrumentStore: InstrumentStore,
  subscribeToInstrumentChanges: SubscribeToInstrumentChangesFn,
  subscriptionParams?: InstrumentSubscriptionProps,
) => {
  const [instrumentAtom, setInstrumentAtom] = useState(instrumentStore.getInstrumentAtom(instrument));

  const previousInstrumentChangesListener = useRef<(() => void) | null>(null);
  const previousInstrument = useRef<ListedInstrument | null>(null);
  const previousSubscriptionParams = useRef<InstrumentSubscriptionProps | undefined>(undefined);

  useEffect(() => {
    if (!subscriptionParams || !instrument || subscriptionParams.disableSubscription) {
      return;
    }
    const { source, dataScope, forceRequestStaticData } = subscriptionParams;

    // this useEffect runs too frequently, so we need to check if the subscription params have changed using deep comparison
    if (previousInstrumentChangesListener.current && previousInstrument.current) {
      if (
        getInstrumentIdForce(instrument) === getInstrumentIdForce(previousInstrument.current) &&
        previousSubscriptionParams.current?.source === source
      ) {
        return;
      } else {
        // if the instrument has changed, we need to unsubscribe from the previous instrument
        previousInstrumentChangesListener.current();
        previousInstrumentChangesListener.current = null;
      }
    }

    previousInstrument.current = instrument;
    previousSubscriptionParams.current = subscriptionParams;

    previousInstrumentChangesListener.current = subscribeToInstrumentChanges(
      getSubscribeOptions(source, instrument, dataScope, forceRequestStaticData),
    );
  }, [subscribeToInstrumentChanges, instrument, subscriptionParams]);

  // update the instrument atom if instrument prop is changing
  useEffect(() => {
    setInstrumentAtom(instrumentStore.getInstrumentAtom(instrument));
  }, [instrument, instrumentStore]);

  // cleanup the subscription if there is any
  useEffect(() => {
    return () => {
      if (previousInstrumentChangesListener.current) {
        previousInstrumentChangesListener.current();
        previousInstrumentChangesListener.current = null;
      }
    };
  }, []);

  return instrumentAtom;
};
